import React, { useEffect, useState } from "react";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
  useAsyncDebounce,
} from "react-table";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Box,
  IconButton,
  Text,
  Select,
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  CloseButton,
} from "@chakra-ui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TriangleDownIcon,
  TriangleUpIcon,
  Search2Icon,
} from "@chakra-ui/icons";

// Styles live in App.scss under .data-table-* and the shared .qset-table classes.

const GlobalFilter = ({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) => {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <InputGroup className="data-table-search">
      <InputLeftElement
        pointerEvents="none"
        children={<Search2Icon color="#8a86a6" />}
      />
      <Input
        type="text"
        aria-label="Search"
        placeholder={`Search ${count} ${count === 1 ? "record" : "records"}...`}
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
      />
      {!!value && (
        <InputRightElement>
          <CloseButton
            size="sm"
            aria-label="Clear search"
            onClick={() => {
              setValue("");
              onChange("");
            }}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
};

export const PaginatedDataTable = ({
  columns,
  data,
  showNumberOfRecords = 10,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    if (showNumberOfRecords) {
      setPageSize(showNumberOfRecords);
    }
  }, [setPageSize, showNumberOfRecords]);
  return (
    <>
      <Flex className="data-table-toolbar">
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
        <Select
          className="data-table-page-size"
          aria-label="Rows per page"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </Select>
      </Flex>
      <Box className="qset-table-wrap">
        <Table className="qset-table" {...getTableProps()}>
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}
                    aria-sort={column.isSorted ? (column.isSortedDesc ? "descending" : "ascending") : undefined}
                    className={column.canSort ? "data-table-sortable" : undefined}
                  >
                    <Flex alignItems="center">
                      {column.render("Header")}
                      {/* Add a sort direction indicator */}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <TriangleUpIcon ml={1} w={3} h={3} />
                        ) : (
                          <TriangleDownIcon ml={1} w={3} h={3} />
                        )
                      ) : (
                        ""
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <Td {...cell.getCellProps()} >{cell.render("Cell")}</Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        {page.length === 0 && (
          <Text className="data-table-empty">
            {globalFilter ? `Nothing matches "${globalFilter}".` : "Nothing to show yet."}
          </Text>
        )}
      </Box>

      {pageCount > 1 && (
        <Flex className="data-table-pagination">
          <IconButton
            className="data-table-page-btn"
            aria-label="First page"
            onClick={() => gotoPage(0)}
            isDisabled={!canPreviousPage}
            icon={<ArrowLeftIcon h={2.5} w={2.5} />}
          />
          <IconButton
            className="data-table-page-btn"
            aria-label="Previous page"
            onClick={previousPage}
            isDisabled={!canPreviousPage}
            icon={<ChevronLeftIcon h={5} w={5} />}
          />
          <Text className="data-table-page-label">
            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
          </Text>
          <IconButton
            className="data-table-page-btn"
            aria-label="Next page"
            onClick={nextPage}
            isDisabled={!canNextPage}
            icon={<ChevronRightIcon h={5} w={5} />}
          />
          <IconButton
            className="data-table-page-btn"
            aria-label="Last page"
            onClick={() => gotoPage(pageCount - 1)}
            isDisabled={!canNextPage}
            icon={<ArrowRightIcon h={2.5} w={2.5} />}
          />
        </Flex>
      )}
    </>
  );
};
